import { Capacitor } from "@capacitor/core"
import { Directory, Filesystem, type DeleteFileOptions } from "@capacitor/filesystem"
import type { Intent } from "@mindlib-capacitor/send-intent"
import { fileTypeFromBuffer } from "file-type"
import { useFileUpload } from "@/composables/file/file-upload"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import type { FileRelation } from "@/models/file/file-relation.model"

const SHARE_CACHE = "send-intent"

/** Stage the whole share before uploading, so interrupted batches stay in one temporary directory. */
export async function uploadSendIntentFiles(
  intents: Intent[],
  onUploaded: (fileRelation: FileRelation) => void
): Promise<boolean> {
  const files: Array<Intent & { temporaryUrl?: string }> = []
  let successful = true
  for (const intent of intents) {
    if (!intent.url || intent.type === "text/plain" || intent.type === "text/html") continue
    const source = await ownedCopy(intent.url)
    if (!source) {
      files.push(intent)
      continue
    }
    try {
      const path = `${SHARE_CACHE}/${Date.now()}-${crypto.randomUUID()}`
      await Filesystem.mkdir({
        directory: Directory.Cache,
        path: SHARE_CACHE,
        recursive: true
      }).catch((error) => {
        if (error.code !== "OS-PLUG-FILE-0010") throw error // The cache directory may already exist.
      })
      const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path })
      await Filesystem.rename({ from: source, to: uri })
      files.push({ ...intent, url: uri, temporaryUrl: uri })
    } catch (error) {
      console.error("Failed to stage shared-file copy:", error)
      await removeCopy(source)
      successful = false
    }
  }
  for (const file of files) {
    try {
      if (!(await uploadFile(file, onUploaded))) successful = false
    } finally {
      // Only the app's temporary copy is removed; the external source stays intact.
      if (file.temporaryUrl) await removeCopy(file.temporaryUrl)
    }
  }
  return files.length > 0 && successful
}

export function uploadSendIntentFile(
  intent: Intent,
  onUploaded: (fileRelation: FileRelation) => void
): Promise<boolean> {
  return uploadSendIntentFiles([intent], onUploaded)
}

async function uploadFile(
  intent: Intent,
  onUploaded: (fileRelation: FileRelation) => void
): Promise<boolean> {
  if (!intent.url || intent.type === "text/plain" || intent.type === "text/html") return false

  try {
    const resultUrl = decodeURIComponent(intent.url)
    const content = await Filesystem.readFile({ path: resultUrl })
    if (!content.data) {
      console.error("No data received from file read")
      return false
    }

    const decodedStr = atob(content.data as string)
    const binaryData = Uint8Array.from(decodedStr, (c) => c.charCodeAt(0))
    const type = await fileTypeFromBuffer(binaryData)
    if (!type) {
      console.error("Could not determine file type from buffer")
      return false
    }

    const dataUrl = `data:${type.mime};base64,${content.data}`
    const base64Response = await fetch(dataUrl)
    if (!base64Response.ok) {
      console.error("Failed to fetch data URL:", base64Response.statusText)
      return false
    }

    const blob = await base64Response.blob()
    if (blob.size === 0) {
      console.error("Received empty blob")
      return false
    }

    const file = new File([blob], `media-cache.${type.ext}`, { type: type.mime })
    const { upload } = useFileUpload(false)
    await upload(file, FileUploadContexts.SERVICE_PROVIDER_APP, onUploaded)
    return true
  } catch (error) {
    console.error("Error processing send intent:", error)
    return false
  }
}

/** Only the flat copy directories used by our share receivers are eligible. */
async function ownedCopy(url: string): Promise<string | undefined> {
  try {
    // The iOS extension may pass a percent-encoded URL through the deep link.
    const value = url.startsWith("file:") ? url : decodeURIComponent(url)
    if (
      decodeURIComponent(value)
        .split("/")
        .some((part) => part === "." || part === "..")
    )
      return
    const file = new URL(value)
    if (file.protocol !== "file:" || file.host || file.search || file.hash) return
    const path = decodeURIComponent(file.pathname)
    let root: string
    let location: DeleteFileOptions
    if (Capacitor.getPlatform() === "android") {
      const { uri } = await Filesystem.getUri({ directory: Directory.Data, path: "" })
      root = `${decodeURIComponent(new URL(uri).pathname).replace(/\/$/, "")}/`
      location = { directory: Directory.Data, path: path.slice(root.length) }
    } else if (Capacitor.getPlatform() === "ios") {
      // iOS grants access only to this app's entitled App Group container. Never
      // accept Documents, Inbox, DCIM, or a child directory of the shared container.
      const group = path.match(/^(.+\/Containers\/Shared\/AppGroup\/[0-9a-f-]{36}\/+)/i)
      if (!group) return
      root = group[1]
      location = { path: file.href }
    } else return
    if (!path.startsWith(root)) return
    const name = path.slice(root.length)
    if (!name || name === "." || name === ".." || name.includes("/") || name.includes("\\")) return
    const stat = await Filesystem.stat(location)
    if (stat.type === "file") return file.href
  } catch {
    return undefined
  }
}

async function removeCopy(url: string): Promise<void> {
  try {
    await Filesystem.deleteFile({ path: url })
  } catch (error) {
    if (!isMissing(error)) console.warn("Failed to remove shared-file copy:", error)
  }
}

function isMissing(error: unknown): boolean {
  return (error as { code?: string })?.code === "OS-PLUG-FILE-0008"
}

/** Remove abandoned copies after 24 hours, preserving recent share activities. */
export async function cleanupSendIntentFiles(): Promise<void> {
  if (Capacitor.getPlatform() === "web") return
  try {
    const { files } = await Filesystem.readdir({ directory: Directory.Cache, path: SHARE_CACHE })
    for (const file of files) {
      const match = file.name.match(/^(\d+)-[0-9a-f-]{36}$/i)
      if (file.type !== "file" || !match || Date.now() - Number(match[1]) < 24 * 60 * 60 * 1000)
        continue
      try {
        await Filesystem.deleteFile({
          directory: Directory.Cache,
          path: `${SHARE_CACHE}/${file.name}`
        })
      } catch (error) {
        if (!isMissing(error)) console.warn("Failed to remove abandoned shared-file copy:", error)
      }
    }
  } catch (error) {
    if (!isMissing(error)) console.warn("Failed to inspect shared-file cache:", error)
  }
}
